import React, { useRef } from 'react';
import SectionWrapper from './SectionWrapper';
import { useAppData } from '../../context/AppContext';
import { exportIndicesPDF } from '../../utils/pdf';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';

export default function OvernightIndices() {
  const { currentDaily, updateCurrentDaily, currentDate } = useAppData();
  const indices = currentDaily.overnightIndices || [];
  
  let headers = currentDaily.overnightIndicesHeaders;
  if (!headers || headers.length === 0) {
    if (indices.length > 0 && indices[0].name !== undefined) {
      headers = ['name', 'close', 'change', 'pctChange', 'notes'];
    } else if (indices.length > 0) {
      headers = Object.keys(indices[0]).filter(k => k !== 'id');
    } else {
      headers = ['Instrument', 'Close', 'Change', 'Change %', 'Notes'];
    }
  }

  const fileInputRef = useRef(null);

  const updateRow = (id, field, value) => {
    updateCurrentDaily(prev => ({
      ...prev,
      overnightIndices: prev.overnightIndices.map(i => i.id === id ? { ...i, [field]: value } : i)
    }));
  };

  const handleDownload = () => {
    exportIndicesPDF(format(currentDate, 'EEEE, dd MMMM yyyy'), indices, headers);
  };

  const getStatusColor = (val, header) => {
    if (!val) return 'text-app-text';
    const cleanVal = val.toString().trim();
    const hLower = header.toLowerCase();
    const isChangeCol = hLower.includes('change') || hLower.includes('chg') || hLower.includes('%');

    if (cleanVal.startsWith('+')) return 'text-app-green font-bold';
    if (cleanVal.startsWith('-')) return 'text-app-red font-bold';

    if (isChangeCol) {
      const num = parseFloat(cleanVal.replace(/[^0-9.-]/g, ''));
      if (num > 0) return 'text-app-green font-bold';
      if (num < 0) return 'text-app-red font-bold';
    }

    return 'text-app-text';
  };

  const formatHeader = (h) => {
    const map = { name: 'Instrument', close: 'Close', change: 'Change', pctChange: 'Change %', notes: 'Notes' };
    return map[h] || h;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
      if (lines.length < 2) return;

      const splitCsv = (str) => {
        const result = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) { result.push(cur); cur = ''; }
          else cur += char;
        }
        result.push(cur);
        return result.map(v => v.trim().replace(/^"|"$/g, ''));
      };

      const extractedHeaders = splitCsv(lines[0]).filter(h => h);
      if (!extractedHeaders.some(h => h.toLowerCase() === 'notes')) extractedHeaders.push('Notes');

      const importedData = [];
      for (let i = 1; i < lines.length; i++) {
        const values = splitCsv(lines[i]);
        if (values.length > 0 && values.some(v => v)) {
          const rowObj = { id: uuidv4() };
          extractedHeaders.forEach((h, index) => {
            rowObj[h] = values[index] !== undefined ? values[index] : '';
          });
          importedData.push(rowObj);
        }
      }

      if (importedData.length > 0) {
        updateCurrentDaily(prev => ({
          ...prev,
          overnightIndicesHeaders: extractedHeaders,
          overnightIndices: importedData
        }));
      }
    } catch (err) {
      console.error(err);
    }
    e.target.value = '';
  };

  const addManualRow = () => {
    const newRow = { id: uuidv4() };
    headers.forEach((h, i) => { newRow[h] = i === 0 ? 'NEW TICKER' : ''; });
    updateCurrentDaily(prev => ({
      ...prev,
      overnightIndices: [...(prev.overnightIndices || []), newRow]
    }));
  };

  return (
    <SectionWrapper title="Overnight Indices" onDownload={handleDownload}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4 border-b border-app-border/40 pb-6">
        <div className="space-y-2">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-app-accent flex items-center gap-2">
            <FiIcons.FiInfo /> Dynamic CSV Sync
          </p>
          <p className="text-sm text-app-muted max-w-xl leading-relaxed">
            Upload your daily <span className="text-white font-bold">.csv</span> export. The terminal will automatically map headers and apply color-coded analysis to the incoming data stream.
          </p>
        </div>
        
        <input type="file" accept=".csv,.txt" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
        <button 
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-3 text-xs font-black uppercase tracking-widest bg-app-accent text-black px-6 py-3.5 rounded-xl hover:bg-yellow-500 transition-all shadow-xl shadow-app-accent/10 shrink-0"
        >
          <FiIcons.FiUploadCloud size={18} /> Import Data
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left whitespace-nowrap">
          <thead>
            <tr className="text-app-muted border-b border-app-border bg-app-bg/50">
              {headers.map(h => (
                <th key={h} className="py-4 px-3 font-bold uppercase tracking-widest text-xs">
                  {formatHeader(h)}
                </th>
              ))}
              <th className="py-4 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border/30">
            {indices.map(row => (
              <tr key={row.id} className="hover:bg-white/[0.02] transition-colors group">
                {headers.map((h, idx) => (
                  <td key={h} className="py-3 px-3">
                    <input 
                      type="text" 
                      value={row[h] || ''} 
                      onChange={e => updateRow(row.id, h, e.target.value)} 
                      placeholder={formatHeader(h)}
                      className={`w-full bg-transparent border-transparent hover:border-app-border focus:border-app-accent focus:bg-app-bg/50 px-3 py-2 rounded transition-all ${idx === 0 ? 'font-bold text-sm uppercase' : 'font-mono text-sm'} ${getStatusColor(row[h], h)}`}
                    />
                  </td>
                ))}
                <td className="py-3 px-3 text-right">
                  <button 
                    onClick={() => {
                      updateCurrentDaily(prev => ({
                        ...prev,
                        overnightIndices: prev.overnightIndices.filter(i => i.id !== row.id)
                      }));
                    }}
                    className="opacity-0 group-hover:opacity-100 p-2 text-app-muted hover:text-app-red transition-all"
                  >
                    <FiIcons.FiX size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <button 
          onClick={addManualRow}
          className="mt-6 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-app-muted hover:text-app-accent transition-colors"
        >
          <FiIcons.FiPlus size={16} /> Add Manual Entry
        </button>
      </div>
    </SectionWrapper>
  );
}